import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './AddTourist.css';

const AddTourist = () => {
  const [touristData, setTouristData] = useState({ title: '', description: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setTouristData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTouristData({ ...touristData, image: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure all fields are filled
    if (!touristData.title || !touristData.description || !touristData.image) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please provide title, description, and image for the tourist entry.',
        confirmButtonColor: '#5e71f6',
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('title', touristData.title);
    formData.append('description', touristData.description);
    formData.append('image', touristData.image);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/add-tourist`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setLoading(false);

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Tourist information added successfully!',
        confirmButtonColor: '#5e71f6',
      });

      navigate('/admin/remove-tourist');
      setTouristData({ title: '', description: '', image: null });
      setImagePreview(null);
    } catch (error) {
      setLoading(false);
      console.error("Error uploading tourist data:", error);
      
      Swal.fire({
        icon: 'error',
        title: 'Failed!',
        text: 'Failed to add tourist information.',
        confirmButtonColor: '#5e71f6',
      });
    }
  };

  return (
    <div className="add-tourist-wrapper">
      <div className="add-tourist-container">
        <div className="header-section">
          <h1 className="page-title">Add Tourist Place</h1>
          <p className="page-subtitle">Add new tourist attraction and places of interest</p>
        </div>

        <form onSubmit={handleSubmit} className="tourist-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={touristData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              placeholder="Enter tourist place name (e.g., Cox's Bazar Beach)"
              className="form-input"
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              value={touristData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows="6"
              required
              placeholder="Enter description, attractions, and other details about the place..."
              className="form-textarea"
            />
          </div>

          {/* Image Upload */}
          <div className="image-upload-card">
            <label className="form-label">
              Upload Image <span className="required">*</span>
            </label>
            <div className="image-upload-area">
              <input
                type="file"
                id="image-upload"
                onChange={handleImageChange}
                accept="image/*"
                required
                className="image-input"
              />
              <label htmlFor="image-upload" className="image-upload-label">
                {imagePreview ? (
                  <div className="image-preview-container">
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                    <div className="image-overlay">
                      <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="change-text">Click to change image</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="upload-text">Click to upload or drag and drop</span>
                    <span className="upload-hint">PNG, JPG, JPEG (MAX. 5MB)</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <div className="loading-spinner-small"></div>
                Adding Tourist Place...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Add Tourist Place
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddTourist;
